/**
 * Card Image Utilities für Splinterlands Karten
 * Enthält Funktionen zur korrekten Bestimmung der Image-URLs für verschiedene Kartentypen
 */

// Debug-Modus für detaillierte Logging (kann in Produktion auf false gesetzt werden)
export const DEBUG_MODE = true;

/**
 * Debug-Logger für Card Image Utilities
 * Gibt Logs nur aus, wenn DEBUG_MODE aktiviert ist
 */

/**
 * URLs für Karteneffekte und Overlays
 */
export const CARD_EFFECTS = {
  ARCANA_GLIMMER: 'https://d36mxiodymuqjm.cloudfront.net/card_effects/arcane_glimmer_full.webp'
};

/**
 * Splinterlands Bildpfade basierend auf der offiziellen API-Dokumentation
 * Quelle: https://docs.splinterlands.com/
 */
const CARD_IMAGE_BASE_URL = 'https://d36mxiodymuqjm.cloudfront.net';
// Einheitlicher Basispfad für alle Karten
const CARD_IMAGE_BASE_PATH = '/cards_by_level/';

const CARD_IMAGE_PATHS = {
  LEGACY_PATH: CARD_IMAGE_BASE_PATH,            // Einheitlich für alle Editionen
  UNTAMED_PATH: CARD_IMAGE_BASE_PATH,           // Vorher '/cards_untamed/'
  REBELLION_PATH: CARD_IMAGE_BASE_PATH,         // Vorher '/cards_rebellion/'
  MODERN_PATH: '/cards_v2.2/'                   // Moderner Pfad für Conclave Arcana & Escalation special foils
};

// Edition-to-folder mapping (angepasst für einheitlichen Pfad)
const EDITION_TO_IMAGE_PATH = {
  0: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'alpha', extension: 'png' },      // Alpha
  1: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'beta', extension: 'png' },       // Beta
  2: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'promo', extension: 'png' },      // Promo
  3: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'reward', extension: 'png' },     // Reward
  4: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'untamed', extension: 'png' },    // Untamed - jetzt mit folder
  5: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'dice', extension: 'png' },       // Dice - jetzt mit folder
  6: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'gladius', extension: 'png' },     // Gladius (Soulbound)
  7: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'chaos', extension: 'png' },      // Chaos Legion
  8: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'rift', extension: 'png' },       // Riftwatchers
  10: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'soulbound', extension: 'png' }, // Soulbound
  12: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'rebellion', extension: 'png' }, // Rebellion - jetzt mit folder und png
  14: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'conclave', extension: 'png' },   // Conclave Arcana - jetzt mit folder und png
  15: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'foundations', extension: 'png' }, // Foundations
  17: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'extra', extension: 'png' },   // Conclave Arcana Extra Cards (uses 'extra' folder)
  19: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'land', extension: 'png' },       // Land
  20: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'escalation', extension: 'png' }, // Escalation (now uses standard path with level)
  21: { path: CARD_IMAGE_PATHS.LEGACY_PATH, folder: 'verico', extension: 'png' }      // Verico (Land Sub-Set) - eigener Ordner, gleiches Level-Schema
};

// Spezielle Mapping für problematische Karten mit bekannten Pfaden
// Diese Liste hilft, Karten direkt zuzuordnen, die in der normalen Logik nicht funktionieren
const SPECIAL_CARD_PATHS = {
  // Format: "Kartenname" (ohne Leerzeichen): { urlPattern: String, edition: Number, altPatterns: [String] }
  
  // Bekannte problematische Karten
  "ByzantineKitty": { 
    urlPattern: `${CARD_IMAGE_BASE_URL}${CARD_IMAGE_PATHS.UNTAMED_PATH}Byzantine%20Kitty`, 
    edition: 6,
    altPatterns: [
      `${CARD_IMAGE_BASE_URL}/cards_by_level/untamed/Byzantine%20Kitty`
    ]
  },
  "TheorosaNightshade": { 
    urlPattern: `${CARD_IMAGE_BASE_URL}${CARD_IMAGE_PATHS.UNTAMED_PATH}Theorosa%20Nightshade`, 
    edition: 6,
    altPatterns: [
      `${CARD_IMAGE_BASE_URL}/cards_by_level/untamed/Theorosa%20Nightshade`
    ]
  },
  "ArcaneSkinwalker": { 
    urlPattern: `${CARD_IMAGE_BASE_URL}${CARD_IMAGE_PATHS.LEGACY_PATH}conclave/Arcane%20Skinwalker`, 
    edition: 2, // Promo card but using conclave folder
    altPatterns: [
      `${CARD_IMAGE_BASE_URL}/cards_by_level/conclave/Arcane%20Skinwalker`
    ]
  },
  "GrameltheHunger": { 
    urlPattern: `${CARD_IMAGE_BASE_URL}${CARD_IMAGE_PATHS.LEGACY_PATH}conclave/Gramel%20the%20Hunger`, 
    edition: 2, // Promo card but using conclave folder
    altPatterns: [
      `${CARD_IMAGE_BASE_URL}/cards_by_level/conclave/Gramel%20the%20Hunger`
    ]
  },
  "ArchmageYabanius": {
    urlPattern: `${CARD_IMAGE_BASE_URL}${CARD_IMAGE_PATHS.LEGACY_PATH}conclave/Archmage%20Yabanius`,
    edition: 2, // Promo card but using conclave folder
    altPatterns: [
      `${CARD_IMAGE_BASE_URL}/cards_by_level/conclave/Archmage%20Yabanius`
    ]
  },
  // Edition 17 exception: Darkguard Hellion uses cards_v2.2 format without levels
  "DarkguardHellion": {
    urlPattern: `${CARD_IMAGE_BASE_URL}${CARD_IMAGE_PATHS.MODERN_PATH}Darkguard%20Hellion`,
    edition: 17,
    extension: 'jpg',
    noLevel: true
  },
  // Note: Most Edition 17 cards use 'extra' folder with levels
  // Darkguard Hellion is an exception using cards_v2.2 format
  // Note: SPECIAL_CARD_PATHS were previously used as workarounds for cards with incorrect edition data
  // With the new edition mapping system in place, most of these should no longer be needed
  // Keep only truly special cases that can't be handled by the systematic solution
  
  // Hier können weitere problematische Karten hinzugefügt werden, die wirklich spezielle Behandlung brauchen
};

/**
 * Prüft, ob die Karte eine Black Foil-Karte ist (foil=2)
 * Hinweis: foil=2 kann je nach Edition verschiedene Bedeutungen haben:
 * - Edition 14 (Conclave Arcana): Gold Arcana
 * - Edition 19 (Escalation): Black Foil
 * @param {Object} card - Kartenobjekt
 * @returns {Boolean} - true wenn foil=2
 */
export const isBlockfoil = (card) => {
  return card?.foil === 2;
};

/**
 * Prüft, ob eine Gold Black Foil (doppelt-spezielle Karte) vorliegt
 * @param {Object} card - Kartenobjekt
 * @returns {Boolean} - true wenn Gold Black Foil
 */
export const isGoldBlockfoil = (card) => {
  return card?.gold && card?.foil === 2;
};

/**
 * Prüft, ob die Karte eine Gold Arcana-Karte ist (foil=2)
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @returns {Boolean} - true wenn Gold Arcana
 */
export const isGoldArcana = (card, details = {}) => {
  const edition = details?.edition ?? card?.edition;
  return edition === 14 && card?.foil === 2;
};

/**
 * Prüft, ob die Karte eine Black Arcana-Karte ist (foil=4)
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @returns {Boolean} - true wenn Black Arcana
 */
export const isBlackArcana = (card, details = {}) => {
  const edition = details?.edition ?? card?.edition;
  return edition === 14 && card?.foil === 4;
};

/**
 * Prüft, ob die Karte eine Conclave Arcana Karte ist, die ein Glimmer-Overlay benötigt
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @returns {Boolean} - true wenn Conclave Arcana mit Glimmer-Effekt
 */
export const needsGlimmerEffect = (card, details = {}) => {
  if (!card) return false;
  const edition = details?.edition ?? card?.edition;
  
  // Only Conclave Arcana cards (edition 14) with foil types 2 (Gold Arcana) or 4 (Black Arcana)
  return edition === 14 && (card.foil === 2 || card.foil === 4);
};

/**
 * Prüft, ob die Karte eine Arcana-Karte ist (Edition 10)
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @returns {Boolean} - true wenn Arcana
 */
export const isArcana = (card, details = {}) => {
  // Check if card is Arcana by edition or type
  const isArcanaEdition = details?.edition === 10 || details?.edition === 14 || 
                          card?.edition === 10 || card?.edition === 14;
  const isArcanaType = details?.type === 'Arcana' || card?.type === 'Arcana';
  
  // Check for special foil types (2=Gold Arcana, 3=Black Foil, 4=Black Arcana)
  const isArcanaFoil = card?.foil === 2 || card?.foil === 3 || card?.foil === 4;
  
  return isArcanaEdition || isArcanaType || isArcanaFoil;
};

/**
 * Prüft, ob die Karte das neue URL-Format verwendet (cards_v2.2)
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @returns {Boolean} - true wenn neue URL-Format verwendet wird
 */
export const usesNewUrlFormat = (card, details = {}) => {
  return isArcana(card, details) || isBlockfoil(card);
};

/**
 * Bestimmt das richtige Präfix basierend auf der Kartenedition
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @returns {String} - Präfix für die URL
 */
export const getCardPrefix = (card, details) => {
  const edition = details?.edition;
  
  // Direkte Abbildung anhand der bekannten Edition aus den Details
  if (edition !== undefined && EDITION_TO_IMAGE_PATH[edition]) {
    return EDITION_TO_IMAGE_PATH[edition].folder;
  }
  
  // Fallback basierend auf anderen verfügbaren Informationen
  if (card?.edition !== undefined && EDITION_TO_IMAGE_PATH[card.edition]) {
    return EDITION_TO_IMAGE_PATH[card.edition].folder;
  }
  
  // Direkte Überprüfung auf spezifische Edition basierend auf verfügbaren Details
  if (details?.id) {
    // Alpha-Karten haben IDs unter 100
    if (details.id < 100) return 'alpha';
    
    // Beta-Karten haben normalerweise IDs zwischen 100-200
    if (details.id >= 100 && details.id < 200) return 'beta';
  }
  
  return 'reward'; // Fallback nur wenn wirklich keine Edition ermittelt werden kann
};

/**
 * Ermittelt den richtigen Suffix für den Kartentyp
 * @param {Object} card - Kartenobjekt
 * @param {String} level - Kartenlevel ('lv1' oder 'lv4')
 * @param {Boolean} useNewFormat - Ob das neue Format verwendet wird
 * @returns {String} - Suffix für die URL
 */
export const getCardSuffix = (card, level = 'lv1', useNewFormat = false) => {
  // Im neuen Format:
  if (useNewFormat) {
    if (isBlockfoil(card)) {
      return card.gold ? '_gold_blk' : '_blk'; // Black Foil Suffix
    }
    // Arcana und andere spezielle Karten haben
    // einfach nur _gold oder gar keinen Suffix
    return card.gold ? '_gold' : '';
  }
  
  // Im alten Format (cards_by_level):
  const typeSuffix = [];
  
  // Level hinzufügen (für alte Format-URLs)
  typeSuffix.push(level);
  
  // Gold-Status
  if (card.gold) {
    typeSuffix.push('gold');
  }
  
  return typeSuffix.length > 0 ? '_' + typeSuffix.join('_') : '';
};

/**
 * Erstellt eine URL für das Kartenbild basierend auf den definierten Bildpfaden für jede Edition
 * @param {Object} card - Kartenobjekt
 * @param {Object} details - Detailinfos der Karte
 * @param {Boolean} showBaseImages - Ob Basis- oder Level-4-Bilder angezeigt werden sollen (fallback wenn kein level angegeben)
 * @param {Number} customLevel - Optionales spezifisches Level (überschreibt showBaseImages)
 * @returns {String} - URL zum Kartenbild
 */
export const getCardImageUrl = (card, details, showBaseImages, customLevel = null) => {
  if (!card) return '';
  
  // Bestimme das Level: 
  // 1. Verwende customLevel wenn angegeben
  // 2. Verwende card.level aus der API (falls vorhanden)
  // 3. Fallback auf showBaseImages (lv1 vs lv4)
  let level;
  if (customLevel !== null) {
    level = `lv${customLevel}`;
  } else if (card.level && card.level > 0) {
    level = `lv${card.level}`;
  } else {
    level = showBaseImages ? 'lv1' : 'lv4';
  }

  const cardName = (details?.name || card.card_name || '').replace(/ /g, '%20');
  if (!cardName) return '';
  
  // Prüfe, ob es sich um eine spezielle Karte handelt
  const cardNameWithoutSpaces = (details?.name || card.card_name || '').replace(/\s+/g, "");
  if (cardNameWithoutSpaces && SPECIAL_CARD_PATHS[cardNameWithoutSpaces]) {
    const specialCard = SPECIAL_CARD_PATHS[cardNameWithoutSpaces];
    
    // Spezielle Behandlung für Blk-Varianten (foil 3 oder 4)
    let suffix = '';
    if (card.foil === 3 || card.foil === 4) {
      suffix = '_blk'; // Black Foil oder Black Arcana
    } else if (card.gold) {
      suffix = '_gold'; // Gold-Variante
    }
    
    // Wenn noLevel Flag gesetzt ist (neue cards_v2.2 Format), kein Level in URL
    if (specialCard.noLevel) {
      const extension = specialCard.extension || 'png';
      return `${specialCard.urlPattern}${suffix}.${extension}`;
    }
    
    // Für spezielle Karten mit custom extension (z.B. jpg)
    const extension = specialCard.extension || 'png';
    return `${specialCard.urlPattern}_${level}${suffix}.${extension}`;
  }

  // Bestimme die Edition aus den verfügbaren Daten
  const cardEdition = details?.edition ?? card?.edition;
  
  // Wenn keine Edition gefunden wurde, verwende ein Fallback-Bild
  if (cardEdition === undefined && !details?.id) {
    return DEFAULT_CARD_IMAGE;
  }

  // Hole die Bildpfad-Informationen für die Edition
  const pathInfo = EDITION_TO_IMAGE_PATH[cardEdition];
  
  if (pathInfo) {
    // Bestimme den Ordnerpfad
    const folder = pathInfo.folder || '';
    const folderPath = folder ? `${folder}/` : '';
    
    // Bestimme den Suffix basierend auf den Kartenattributen
    let suffix = '';
    
    // Spezielle Behandlung für Black Foil und Arcana-Typen
    let levelStr = level; // Standard: Level wird verwendet
    
    if (cardEdition === 14) { // Conclave Arcana
      if (card.foil === 2) {
        suffix = '_blk'; // Black Foil (foil=2) - bei Escalation: Black Foil, bei Conclave: Gold Arcana
        // Für Black Foil in cards_v2.2 Format: kein Level in der URL
        if (pathInfo.path === CARD_IMAGE_PATHS.MODERN_PATH) {
          levelStr = ''; // Kein Level für Black Foil im modernen Format
        }
      } else if (card.foil === 3 || card.foil === 4) {
        suffix = '_blk'; // Black Foil oder Black Arcana
        // Für Black Foil/Arcana in cards_v2.2 Format: kein Level in der URL
        if (pathInfo.path === CARD_IMAGE_PATHS.MODERN_PATH) {
          levelStr = ''; // Kein Level für Black Foil im modernen Format
        }
      } else if (card.gold) {
        suffix = '_gold'; // Gold-Variante
        // Gold-Varianten im modernen Format haben auch kein Level
        if (pathInfo.path === CARD_IMAGE_PATHS.MODERN_PATH) {
          levelStr = '';
        }
      }
    } else if (card.gold) {
      suffix = '_gold'; // Standard Gold-Variante für alle anderen Editionen
    }
    
    // Generiere die vollständige URL
    // Wenn levelStr leer ist, füge keinen Unterstrich vor dem Suffix hinzu
    const levelPart = levelStr ? `_${levelStr}` : '';
    return `${CARD_IMAGE_BASE_URL}${pathInfo.path}${folderPath}${cardName}${levelPart}${suffix}.${pathInfo.extension}`;
  }
  
  // Fallback für unbekannte Editionen - verwende den generischen Pfad
  return `${CARD_IMAGE_BASE_URL}/cards_by_level/reward/${cardName}_${level}${card.gold ? '_gold' : ''}.png`;
};

/**
 * Prüft, ob die gegebene URL zu einem Kartenbild gehört
 * @param {String} url - Die zu prüfende URL
 * @returns {Boolean} - true wenn es sich um eine Kartenbild-URL handelt
 */
export const isCardImageUrl = (url) => {
  return url.startsWith(CARD_IMAGE_BASE_URL) && 
         (url.includes('/cards_by_level/'));
};

/**
 * Prüft, ob die gegebene URL zu einem Kartenbild der neuen Generation gehört
 * @param {String} url - Die zu prüfende URL
 * @returns {Boolean} - true wenn es sich um eine neue Kartenbild-URL handelt
 */
export const isNewGenCardImageUrl = (url) => {
  return url.startsWith(CARD_IMAGE_BASE_URL + CARD_IMAGE_PATHS.MODERN_PATH);
};

/**
 * Returns the name of a foil type based on its ID
 * @param {Number} foilId - The foil type ID
 * @returns {String} - The name of the foil type
 */
export const getFoilTypeName = (foilId) => {
  switch(foilId) {
    case 0: return 'Regular';
    case 1: return 'Gold Foil';
    case 2: return 'Gold Arcana';
    case 3: return 'Black Foil';
    case 4: return 'Black Arcana';
    default: return 'Unknown';
  }
};

/**
 * Default image for cards that fail to load
 */
export const DEFAULT_CARD_IMAGE = 'https://via.placeholder.com/350x490.png?text=Card+Image+not+found';
